<template>
  <Teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <!-- Close Button -->
        <button class="close-btn" @click="$emit('close')">
          <span class="material-symbols-outlined">close</span>
        </button>

        <!-- Loading State -->
        <div v-if="loading" class="modal-loading">
          <span class="material-symbols-outlined spinning">sync</span>
          <span>Loading game details...</span>
        </div>

        <!-- Content -->
        <template v-else-if="game">
          <!-- Header -->
          <div class="modal-header">
            <h2 class="modal-title">Game {{ game.room_code }}</h2>
            <span class="status-badge" :class="game.status">
              {{ formatStatus(game.status) }}
            </span>
          </div>

          <!-- Final Scores -->
          <div class="final-scores-section">
            <div class="team-score-card" :class="{ winner: game.winning_team === 'A' }">
              <span class="team-label">{{ game.team_a_name }}</span>
              <span class="team-final-score">{{ game.final_team_a_score.toLocaleString() }}</span>
              <span v-if="game.winning_team === 'A'" class="winner-crown">
                <span class="material-symbols-outlined">emoji_events</span>
              </span>
            </div>

            <span class="vs-divider">VS</span>

            <div class="team-score-card" :class="{ winner: game.winning_team === 'B' }">
              <span class="team-label">{{ game.team_b_name }}</span>
              <span class="team-final-score">{{ game.final_team_b_score.toLocaleString() }}</span>
              <span v-if="game.winning_team === 'B'" class="winner-crown">
                <span class="material-symbols-outlined">emoji_events</span>
              </span>
            </div>
          </div>

          <!-- Players Section -->
          <div class="section">
            <h3 class="section-title">Players</h3>
            <div class="players-grid">
              <div
                v-for="player in game.players"
                :key="player.seat_position"
                class="player-card"
              >
                <div class="player-seat">Seat {{ player.seat_position + 1 }}</div>
                <div class="player-name">
                  {{ player.username || (player.is_bot ? 'Bot' : 'Unknown') }}
                </div>
                <div class="player-team" :class="`team-${player.team.toLowerCase()}`">
                  Team {{ player.team }}
                </div>
                <div v-if="player.is_bot" class="bot-badge">BOT</div>
                <div v-if="player.was_replaced" class="replaced-badge">Left Game</div>
              </div>
            </div>
          </div>

          <!-- Round History -->
          <div v-if="game.rounds.length > 0" class="section">
            <h3 class="section-title">Round History</h3>
            <div class="rounds-table-container">
              <table class="rounds-table">
                <thead>
                  <tr>
                    <th>Round</th>
                    <th>{{ game.team_a_name }}</th>
                    <th>{{ game.team_b_name }}</th>
                    <th>Winner</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="round in game.rounds" :key="round.round_number">
                    <td class="round-num">{{ round.round_number }}</td>
                    <td :class="{ 'round-winner': round.winning_team === 'A' }">
                      {{ round.team_a_score >= 0 ? '+' : '' }}{{ round.team_a_score.toLocaleString() }}
                    </td>
                    <td :class="{ 'round-winner': round.winning_team === 'B' }">
                      {{ round.team_b_score >= 0 ? '+' : '' }}{{ round.team_b_score.toLocaleString() }}
                    </td>
                    <td>
                      <span v-if="round.winning_team" class="round-winner-badge">
                        Team {{ round.winning_team }}
                      </span>
                      <span v-else class="round-tie">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Bot States (for resumable games) -->
          <div v-if="game.bot_states.length > 0 && isResumable" class="section">
            <h3 class="section-title">Bot Status</h3>
            <div class="bot-states">
              <div v-for="bot in game.bot_states" :key="bot.seat_position" class="bot-state-card">
                <span class="bot-name">{{ bot.bot_name }}</span>
                <span class="bot-team">Team {{ bot.team }}</span>
                <span class="bot-cards">
                  {{ bot.hand_count }} cards
                  {{ bot.is_in_foot ? '(in foot)' : '(in hand)' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Game Info -->
          <div class="game-info-footer">
            <div class="info-item">
              <span class="info-label">Created</span>
              <span class="info-value">{{ formatDateTime(game.created_at) }}</span>
            </div>
            <div v-if="game.finished_at" class="info-item">
              <span class="info-label">Finished</span>
              <span class="info-value">{{ formatDateTime(game.finished_at) }}</span>
            </div>
            <div v-if="!game.finished_at" class="info-item">
              <span class="info-label">Current Round</span>
              <span class="info-value">{{ game.current_round }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { GameDetailResponse } from '@/types/game'
import { useGameHistory } from '@/composables/useGameHistory'

const props = defineProps<{
  game: GameDetailResponse | null
  loading: boolean
}>()

defineEmits<{
  (e: 'close'): void
}>()

const { formatStatus } = useGameHistory()

const isResumable = computed(() => {
  return props.game && ['in_progress', 'paused'].includes(props.game.status)
})

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

/* Modal Content */
.modal-content {
  position: relative;
  background: linear-gradient(145deg, #1a1a2e, #16213e);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5);
}

/* Close Button */
.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.close-btn .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Loading */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #e8c547;
  margin: 0;
  letter-spacing: 0.05em;
}

.status-badge {
  padding: 0.375rem 0.875rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.finished {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

.status-badge.in_progress {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.status-badge.paused {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

/* Final Scores */
.final-scores-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 16px;
}

.team-score-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  min-width: 120px;
  position: relative;
}

.team-score-card.winner {
  background: rgba(74, 222, 128, 0.15);
  border: 1px solid rgba(74, 222, 128, 0.3);
}

.team-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.team-final-score {
  font-size: 2rem;
  font-weight: 700;
  color: white;
}

.winner-crown {
  position: absolute;
  top: -0.75rem;
  right: -0.5rem;
  color: #fbbf24;
}

.winner-crown .material-symbols-outlined {
  font-size: 1.5rem;
}

.vs-divider {
  font-size: 1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.3);
}

/* Sections */
.section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 1rem 0;
}

/* Players Grid */
.players-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.player-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 0.875rem;
  position: relative;
}

.player-seat {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.player-name {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.25rem;
}

.player-team {
  font-size: 0.75rem;
  font-weight: 500;
}

.player-team.team-a {
  color: #60a5fa;
}

.player-team.team-b {
  color: #f472b6;
}

.bot-badge,
.replaced-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  text-transform: uppercase;
}

.bot-badge {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.replaced-badge {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

/* Rounds Table */
.rounds-table-container {
  overflow-x: auto;
}

.rounds-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.rounds-table th,
.rounds-table td {
  padding: 0.75rem;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.rounds-table th {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rounds-table td {
  color: rgba(255, 255, 255, 0.8);
}

.round-num {
  font-weight: 600;
  color: #e8c547;
}

.round-winner {
  color: #4ade80;
  font-weight: 600;
}

.round-winner-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: #4ade80;
}

.round-tie {
  color: rgba(255, 255, 255, 0.3);
}

/* Bot States */
.bot-states {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.bot-state-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.2);
  border-radius: 8px;
}

.bot-name {
  font-weight: 600;
  color: #fbbf24;
}

.bot-team {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.bot-cards {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Footer Info */
.game-info-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
}
</style>
